import { useQuery } from "@tanstack/react-query";
import { FileText, Plus, Settings } from "lucide-react";
/**
 * Listado de comprobantes con filtros y links a detalle/emisión.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { type EstadoComprobante, type TipoComprobante, listComprobantes } from "../api/facturacion";
import { useAuth } from "../auth/AuthProvider";
import { Alert, Badge, Button, Card, CardBody, CardHeader } from "../components/ui";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";

const ESTADO_VARIANT: Record<EstadoComprobante, "neutral" | "success" | "warning" | "danger"> = {
  PENDIENTE: "warning",
  ACEPTADO: "success",
  RECHAZADO: "danger",
  ANULADO: "neutral",
};

export function ComprobantesListPage() {
  const tz = useZonaHoraria();
  const base = useBase();
  const { me } = useAuth();
  const puedeEmitir = me?.permisos.includes("comprobante:create");
  const esAdmin = me?.permisos.includes("comprobante:anular");

  const [estado, setEstado] = useState<EstadoComprobante | "">("");
  const [tipo, setTipo] = useState<TipoComprobante | "">("");

  const query = useQuery({
    queryKey: ["comprobantes", { estado, tipo }],
    queryFn: () =>
      listComprobantes({
        estado: (estado || undefined) as EstadoComprobante | undefined,
        tipo_comprobante: (tipo || undefined) as TipoComprobante | undefined,
        limit: 200,
      }),
  });

  const items = query.data ?? [];
  const totalEmitido = useMemo(
    () => items.filter((c) => c.estado === "ACEPTADO").reduce((sum, c) => sum + Number(c.total), 0),
    [items],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Facturación</h1>
          <p className="text-sm text-slate-500 mt-1">Comprobantes electrónicos emitidos al OSE.</p>
        </div>
        <div className="flex items-center gap-2">
          {esAdmin && (
            <Link to={`${base}/facturacion/series`}>
              <Button variant="ghost">
                <Settings className="w-4 h-4" />
                Series
              </Button>
            </Link>
          )}
          {puedeEmitir && (
            <Link to={`${base}/facturacion/emitir`}>
              <Button>
                <Plus className="w-4 h-4" />
                Emitir comprobante
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <select
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoComprobante | "")}
              >
                <option value="">Todos los tipos</option>
                <option value="BOLETA">Boletas</option>
                <option value="FACTURA">Facturas</option>
              </select>
              <select
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={estado}
                onChange={(e) => setEstado(e.target.value as EstadoComprobante | "")}
              >
                <option value="">Todos los estados</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="ACEPTADO">Aceptado</option>
                <option value="RECHAZADO">Rechazado</option>
                <option value="ANULADO">Anulado</option>
              </select>
            </div>
            <div className="text-sm text-slate-600">
              {items.length} comprobantes ·{" "}
              <span className="font-medium text-slate-900">
                S/ {totalEmitido.toFixed(2)} emitido
              </span>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {query.isLoading ? (
            <div className="p-6 text-sm text-slate-500">Cargando…</div>
          ) : query.error ? (
            <div className="p-6">
              <Alert>No se pudieron cargar los comprobantes.</Alert>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No hay comprobantes con esos filtros.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Número</th>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Cliente</th>
                  <th className="text-left px-4 py-2 font-medium">Fecha</th>
                  <th className="text-right px-4 py-2 font-medium">Total</th>
                  <th className="text-center px-4 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">
                      <Link
                        to={`${base}/facturacion/${c.id}`}
                        className="text-[color:var(--color-link)] hover:underline"
                      >
                        {c.numero_completo}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{c.tipo_comprobante}</td>
                    <td className="px-4 py-2 text-slate-800">
                      {c.cliente_razon_social}
                      {c.cliente_numero_doc && (
                        <span className="text-xs text-slate-400 ml-1">
                          ({c.cliente_numero_doc})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {fechaHora(c.fecha_emision, tz)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-slate-900">
                      {c.moneda === "PEN" ? "S/" : "$"} {c.total}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Badge variant={ESTADO_VARIANT[c.estado]}>{c.estado}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
